import React, { useState, useEffect, useMemo } from 'react'; 
import { Link } from 'react-router-dom'; 
import { db } from '../../firebase';
import { collection, query, orderBy, onSnapshot, getDocs } from 'firebase/firestore';

// Helper function: Month format
const formatMonth = (yearMonthString) => { 
    if (!yearMonthString || yearMonthString.split('-').length < 2) return "N/A";
    
    const [year, month] = yearMonthString.split('-');
    const date = new Date(year, parseInt(month) - 1, 1); 
    
    const monthName = date.toLocaleString('en-US', { month: 'long' }).toUpperCase();
    const yearShort = date.getFullYear().toString().slice(-2);
    
    return `${monthName} ${yearShort}`;
};

// Helper function: Currency format
const formatCurrency = (num) => {
  if (typeof num !== 'number') { num = parseFloat(num || 0); }
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};


const FullReport = () => {
    const [allInvoices, setAllInvoices] = useState([]); 
    const [allPayments, setAllPayments] = useState([]); 
    const [loading, setLoading] = useState(true); 
    const [searchQuery, setSearchQuery] = useState('');
    
    const [companies, setCompanies] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState('');
    
    useEffect(() => {
        setLoading(true);
        
        const fetchCompanies = async () => {
            try {
                const companiesSnapshot = await getDocs(query(collection(db, 'companies'), orderBy('name')));
                setCompanies(companiesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            } catch (error) {
                console.error("Error fetching companies: ", error);
            }
        };
        fetchCompanies();
        
        const invoicesQuery = query(collection(db, 'invoices'), orderBy('invoiceYearMonth', 'desc'));

        const unsubInvoices = onSnapshot(invoicesQuery, (querySnapshot) => {
            const invoicesList = querySnapshot.docs.map(doc => ({ 
                id: doc.id, 
                ...doc.data() 
            }));
            setAllInvoices(invoicesList);
            setLoading(false); 
        }, (error) => {
            console.error("Error fetching invoices real-time: ", error);
            setLoading(false);
        });

        const paymentsQuery = query(collection(db, 'payments'), orderBy('receiptNo', 'asc'));
        const unsubPayments = onSnapshot(paymentsQuery, (querySnapshot) => {
            const paymentsList = querySnapshot.docs.map(doc => ({
                id: doc.id, 
                receiptNo: doc.data().receiptNo
            }));
            setAllPayments(paymentsList);
        }, (error) => {
            console.error("Error fetching payments: ", error);
        });

        return () => {
            unsubInvoices();
            unsubPayments(); 
        };
        
    }, []); 

    // Helper Function: Receipt No(s) পাওয়ার জন্য
    const getReceiptNumbers = (paidEntries) => {
        if (!paidEntries || paidEntries.length === 0) {
            return "-";
        }
        const receiptNos = paidEntries
            .map(entry => {
                const payment = allPayments.find(p => p.id === entry.paymentDocId);
                return payment ? payment.receiptNo : null;
            })
            .filter(Boolean); 
        if (receiptNos.length === 0) return "-";
        return receiptNos.join(', ');
    };
    

    const filteredInvoices = allInvoices.filter(invoice => {
        const searchInvNo = searchQuery.toLowerCase();
        
        const companyMatch = selectedCompany === '' || invoice.companyName === selectedCompany;

        const invoiceNoMatch = searchInvNo === '' || 
            (invoice.invoiceNo && invoice.invoiceNo.toLowerCase().includes(searchInvNo));
            
        return companyMatch && invoiceNoMatch;
    });

    const companyDetails = useMemo(() => {
        if (!selectedCompany) return null;
        return companies.find(c => c.name === selectedCompany);
    }, [selectedCompany, companies]);

    const today = new Date().toLocaleDateString('en-GB');

    return (
        <div className='report-container'> 
            <div className="report-paper-landscape"> 
                
                <div className="print-header">
                    <div className="invoice-header">
                        <h1>TWS AIR INTERNATIONAL</h1>
                        <p>10/3/7 Toyenbee Circular Road, Motijheel C/A, Dhaka-1000, BANGLADESH.</p>
                    </div>
                    
                    <div className="customer-details" style={{marginBottom: '20px', fontSize: '14px', alignItems: 'flex-start'}}>
                        {companyDetails ? (
                            <div>
                                <p><strong>STATEMENT FOR:</strong></p>
                                <p><strong>COMPANY NAME: {companyDetails.name}</strong></p>
                                <p><strong>CUSTOMER NAME: {companyDetails.customerName}</strong></p>
                                <p><strong>ADDRESS: {companyDetails.address}</strong></p>
                            </div>
                        ) : (
                            <div>
                                <p><strong>STATEMENT FOR: All Companies</strong></p>
                            </div>
                        )}
                        <div>
                            <p><strong>REPORT DATE:</strong> {today}</p>
                        </div>
                    </div>
                    <h3 className="bill-title" style={{textAlign: 'center'}}>FULL ACCOUNT STATEMENT</h3>
                </div>

                
                <div className="report-header">
                    <p className='new-invoice-heading'>Full Report</p>
                    <div className="invoice-actions no-print-section"> 
                        <button onClick={() => window.print()}>Print Report</button>
                    </div>
                </div>

                <div className='form-section no-print-section' style={{padding: '15px', marginBottom: '15px'}}>
                    <div className='form-grid' style={{gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
                        <div>
                            <label>Search by Invoice No:</label>
                            <input 
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search Invoice No (e.g., INV-1001)..."
                                style={{width: '100%'}}
                            />
                        </div>
                        <div>
                            <label>Filter by Company:</label>
                            <select 
                                value={selectedCompany} 
                                onChange={(e) => setSelectedCompany(e.target.value)}
                            >
                                <option value="">All Companies</option>
                                {companies.map(company => (
                                    <option key={company.id} value={company.name}>
                                        {company.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            
                <div className='invoice-list' style={{overflowX: 'auto'}}>
                    {loading ? <p>Loading report...</p> : (
                        <table className='invoice-preview-table' style={{fontSize: '12px', display: 'table', overflowX: 'unset'}}>
                            <thead>
                                <tr>
                                    <th>Invoice No.</th>
                                    <th>Receipt No(s).</th>
                                    <th>Date</th>
                                    <th>Company Name</th>
                                    <th>Bill Month</th>
                                    <th>Total Bill (BDT)</th>
                                    <th>Previous Due</th>
                                    <th>Total Received</th>
                                    <th>Amount Due</th>
                                    <th className='no-print'>View</th> 
                                </tr>
                            </thead>
                            <tbody>
                                {filteredInvoices.map(invoice => {
                                    const hasPayments = invoice.paidEntries && invoice.paidEntries.length > 0;
                                    
                                    return (
                                        <tr key={invoice.id}>
                                            <td style={{color: 'red', fontWeight: 'bold'}}>{invoice.invoiceNo || 'N/A'}</td>
                                            <td>{getReceiptNumbers(invoice.paidEntries)}</td>
                                            <td>{invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString('en-GB') : 'N/A'}</td>
                                            <td>{invoice.companyName}</td>
                                            <td>{formatMonth(invoice.invoiceYearMonth)}</td>
                                            <td style={{textAlign: 'right'}}>{formatCurrency(invoice.totalBdt)}</td>
                                            <td style={{textAlign: 'right'}}>{formatCurrency(invoice.previousDue)}</td>
                                            <td style={{textAlign: 'right'}}>{formatCurrency(invoice.totalPaid)}</td>
                                            <td style={{textAlign: 'right'}}>{formatCurrency(invoice.finalTotalBdt)}</td>
                                            
                                            <td className='action-buttons no-print' style={{justifyContent: 'center', gap: '5px'}}> 
                                                <Link to={`/dashboard/invoices/view/${invoice.id}`} className='view-btn'>
                                                    Invoice
                                                </Link>
                                                {hasPayments ? (
                                                    <Link to={`/dashboard/full-report/receipts/${invoice.id}`} className='view-btn' style={{backgroundColor: '#28a745'}}>
                                                        Receipt
                                                    </Link>
                                                ) : (
                                                    <button className='view-btn' disabled style={{backgroundColor: '#6c757d', cursor: 'not-allowed'}}>
                                                        Receipt
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                    {filteredInvoices.length === 0 && !loading && <p>No invoices found for this selection.</p>}
                </div>
            </div>
        </div>
    );
}

export default FullReport;